import { useState } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import { useUserContext } from "../../context/useUserContext";
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { showErrorNotification } from "../../helper/Notifications";
import CustomInput from "../Generic/CustomInput";
import PrimaryButton from "../Generic/PrimaryButton";
import LocalStorage from "../../helper/LocalStorage";
import { ReactComponent as MicrosoftLogo } from "../../assets/icons/microsoftLogo.svg";
import { ReactComponent as GoogleLogo } from "../../assets/icons/googleLogo.svg";
import { ReactComponent as AppleLogo } from "../../assets/icons/appleLogo.svg";

const LoginCard = ({ setIsLoading }) => {
  const { loginUser, getMicrosoftSSOLoginLink, getGoogleSSOLoginLink, getAppleSSOLoginLink } = useUserContext();

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialValues = {
    email: "",
    password: "",
  };

  const [inputValues, setInputvalues] = useState(initialValues);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setInputvalues({
      ...inputValues,
      [name]: value,
    });
  };

  const handleLogin = () => {
    setIsLoading(true);

    // Call login API without OTP initially
    loginUser(
      inputValues.email,
      inputValues.password,
      "",
      function (response) {
        // Check if OTP is required
        if (response.data.otp_required) {
          setIsLoading(false);
          // Password is valid, redirect to OTP page
          navigate("/otp-verification", {
            state: {
              email: inputValues.email,
              password: inputValues.password,
              backTo: location.state?.backTo,
              backToSearch: location.state?.search,
            },
          });
          return;
        }

        // Login successful without OTP
        setIsLoading(false);

        if (location.state && location.state?.backTo != "/") {
          navigate({
            pathname: location.state?.backTo,
            search: createSearchParams(location.state?.search).toString(),
          });
        } else {
          navigate(`/dashboard`);
        }
      },
      function (error) {
        if (error.response?.data?.error === "invalid_grant") {
          showErrorNotification("Provided credentials are invalid");
        }
        if (error.response?.data?.error === "Invalid email or password") {
          showErrorNotification(
            "Your email or password are incorrect!"
          );
        }

        if (error.response?.data?.error === "User is not confirmed") {
          showErrorNotification(
            "Before loging in, please confirm your email account, follow the instructions in the confirmation email. If you can't find the email, check your spam folder."
          );
        }

        setIsLoading(false);
        console.log("error");
      }
    );
  };

  const checkSignInDisabled = () => {
    return inputValues.email.length < 4 || inputValues.password.length < 3;
  };

  const handleSignInBtn = () => {
    handleLogin();
  };

  const handleCreateAccountBtn = () => {
    navigate(`/register`);
  };

  const handleForgotPasswordBtn = () => {
    navigate(`/reset_password`);
  };

  const handleLoginMicrosoftSubmit = () => {
    LocalStorage.setSSOFlag(true);
    setIsLoading(true);

    getMicrosoftSSOLoginLink(
      (response) => {
        const microsoftLink = response.data.redirect_url;
        window.location = microsoftLink;
      },
      (error) => {
        setIsLoading(false);
        showErrorNotification("Something went wrong. Please try again.");
      }
    );
  };

  const handleLoginGoogleSubmit = () => {
    LocalStorage.setSSOFlag(true);
    setIsLoading(true);

    getGoogleSSOLoginLink(
      (response) => {
        const googleLink = response.data.redirect_url;
        window.location = googleLink;
      },
      (error) => {
        setIsLoading(false);
        showErrorNotification("Something went wrong. Please try again.");
      }
    );
  };

  const handleLoginAppleSubmit = () => {
    LocalStorage.setSSOFlag(true);
    setIsLoading(true);

    getAppleSSOLoginLink(
      (response) => {
        const appleLink = response.data.redirect_url;
        window.location = appleLink;
      },
      (error) => {
        setIsLoading(false);
        showErrorNotification("Something went wrong. Please try again.");
      }
    );
  };

  return (
    <Card>
      <TitleContainer>
        <h1>Welcome</h1>
        <p>Authenticate with Google, Microsoft or Apple</p>
      </TitleContainer>
      <SSOButtonsWrapper>
        <SSOButton onClick={handleLoginGoogleSubmit}>
          <GoogleLogo />
          <span>Login with Google</span>
        </SSOButton>
        <SSOButton onClick={handleLoginMicrosoftSubmit}>
          <MicrosoftLogo />
          <span>Log in with Microsoft</span>
        </SSOButton>
        <SSOButton onClick={handleLoginAppleSubmit}>
          <AppleLogo />
          <span>Log in with Apple</span>
        </SSOButton>
      </SSOButtonsWrapper>
      <Divider>
        <DividerLine />
        <DividerText>Or continue with</DividerText>
        <DividerLine />
      </Divider>
      <InputsWrapper>
        <CustomInput
          label={"Email"}
          placeholder="olivia@emotii.com"
          name={"email"}
          value={inputValues.email}
          onChange={handleInputChange}
        />
        <Line>
          <CustomInput
            type={"password"}
            label={"Password"}
            placeholder="Enter your password"
            name={"password"}
            value={inputValues.password}
            onChange={handleInputChange}
            bottomActionText={"Forgot your password?"}
            bottomActionClick={() => handleForgotPasswordBtn()}
          />
        </Line>
      </InputsWrapper>
      <SbmitWrapper>
        <PrimaryButton
          styled={{ width: "100%" }}
          disabled={checkSignInDisabled()}
          text={"Login"}
          onClick={handleSignInBtn}
        />
        <SignUpText>
          Don't have an account?{" "}
          <span onClick={() => handleCreateAccountBtn()}>
            Sign up
          </span>
        </SignUpText>
        <TermsText>
          By continuing, you agree to our{" "}
          <a href="#">Terms of Service</a> and <a href="#">Privacy Policy</a>.
        </TermsText>
      </SbmitWrapper>
    </Card>
  );
};

export default LoginCard;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  padding: 50px;
  align-items: center;
  max-width: 530px;
  width: 100%;
  background: ${lightThemeColors.background};
  gap: 24px;
  border-radius: 10px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: auto;
  margin-top: auto;
  z-index: 1;
  @media (max-width: 650px) {
    width: 100%;
    min-width: unset;
    padding: 30px 20px;
  }
`;
const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 4px;
  width: 100%;
  h1 {
    color: ${lightThemeColors.darkColor};
    font-weight: 700;
    font-size: 32px;
    line-height: 40px;
    margin: 0;
  }
  p {
    color: rgba(102, 102, 102, 1);
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    margin: 0;
  }
`;
const InputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
`;

const ForgotPwd = styled.div`
  width: fit-content;
  p {
    width: fit-content;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    color: ${lightThemeColors.primaryColor};
    cursor: pointer;
  }
`;
const SbmitWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 12px;
`;

const SignUpText = styled.p`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  text-align: center;
  margin: 0;
  span {
    font-weight: 600;
    color: ${lightThemeColors.darkColor};
    cursor: pointer;
    text-decoration: underline;
  }
`;

const TermsText = styled.p`
  color: rgba(153, 153, 153, 1);
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  text-align: center;
  margin: 0;
  a {
    color: rgba(153, 153, 153, 1);
    text-decoration: underline;
    cursor: pointer;
    &:hover {
      color: ${lightThemeColors.darkColor};
    }
  }
`;
const Line = styled.div`
  display: flex;
  gap: 30px;
  align-items: center;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin: 10px 0;
  gap: 10px;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background-color: ${lightThemeColors.borderColor};
`;

const DividerText = styled.span`
  color: ${lightThemeColors.darkColorFaded};
  font-size: 14px;
  font-weight: 400;
  white-space: nowrap;
`;

const SSOButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const SSOButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 14px 20px;
  background: ${lightThemeColors.background};
  border: 1px solid rgba(221, 221, 221, 1);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 15px;
  font-weight: 500;
  color: ${lightThemeColors.darkColor};

  &:hover {
    background: rgba(250, 250, 250, 1);
    border-color: rgba(200, 200, 200, 1);
  }

  &:active {
    transform: scale(0.98);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;
